import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Star, CheckCircle, Trophy, Sparkles, ChevronRight, GraduationCap, Flame } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { GeminiHeader } from '@/components/GeminiHeader';
import { motion } from 'framer-motion';

interface LearningQuest {
  id: string;
  template_id: string;
  status: string;
  completed_at: string | null;
  response_data: Record<string, any>;
  quest_templates: {
    id: string;
    title: string;
    description: string;
    xp_reward: number;
    content: { module_id?: string; slides?: any[] };
  };
}

export default function LearningModules() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [quests, setQuests] = useState<LearningQuest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/');
        return;
      }

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('*')
        .eq('user_id', authUser.id)
        .maybeSingle();

      if (!profile) {
        navigate('/');
        return;
      }

      setUser({
        id: profile.id,
        displayName: profile.display_name,
        role: profile.role,
        team: profile.team,
        xp: profile.total_xp || 0,
        streak: profile.consecutive_safe_days || 0,
        badges: [],
        psychometricVector: [],
        baselineRisk: profile.baseline_risk_score,
        currentRisk: profile.current_risk_score,
        anonymousMode: false,
        createdAt: new Date(profile.created_at),
      });

      const { data: questsData, error } = await supabase
        .from('worker_quests')
        .select(`
          id,
          template_id,
          status,
          completed_at,
          response_data,
          quest_templates!inner(
            id,
            title,
            description,
            xp_reward,
            content,
            intervention_type
          )
        `)
        .eq('worker_id', profile.id)
        .eq('quest_templates.intervention_type', 'micro_learning')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuests((questsData || []) as unknown as LearningQuest[]);
    } catch (error) {
      console.error('Error loading modules:', error);
      toast.error('Gagal memuat modul pembelajaran');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleClick = (quest: LearningQuest) => {
    const moduleId = quest.quest_templates?.content?.module_id;
    if (moduleId) {
      navigate(`/learning/${moduleId}`);
    } else {
      toast.error('Modul tidak ditemukan');
    }
  };

  const pendingQuests = quests.filter(q => q.status === 'pending' || q.status === 'in_progress');
  const completedQuests = quests.filter(q => q.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <GeminiHeader 
        title="Micro Learning" 
        subtitle="Modul pembelajaran interaktif"
      />

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 gap-3"
        >
          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{pendingQuests.length}</p>
                <p className="text-xs text-muted-foreground">Tersedia</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <Trophy className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{completedQuests.length}</p>
                <p className="text-xs text-muted-foreground">Selesai</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Available Modules */}
        {pendingQuests.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 rounded-full bg-amber-500" />
              <h2 className="font-semibold text-foreground">Modul Tersedia</h2>
            </div>
            
            <div className="space-y-3">
              {pendingQuests.map((quest, index) => (
                <motion.div
                  key={quest.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  onClick={() => handleModuleClick(quest)}
                  className="group cursor-pointer"
                >
                  <div className="flex gap-4 p-4 rounded-2xl bg-card/80 hover:bg-card shadow-md shadow-black/5 dark:shadow-black/20 border border-border/50 hover:border-border hover:shadow-lg transition-all duration-200">
                                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                                    <BookOpen className="h-6 w-6 text-muted-foreground" />
                                  </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                        {quest.quest_templates.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {quest.quest_templates.description}
                      </p>
                                      <div className="flex items-center gap-3 mt-3">
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                          <Star className="h-3.5 w-3.5" />
                                          <span className="font-medium">{quest.quest_templates.xp_reward} XP</span>
                                        </div>
                                        <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted/50">
                          {quest.status === 'in_progress' ? 'Dalam Proses' : 'Belum Mulai'}
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Completed Modules */}
        {completedQuests.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 rounded-full bg-emerald-500" />
              <h2 className="font-semibold text-foreground">Sudah Selesai</h2>
            </div>
            
            <div className="space-y-2">
              {completedQuests.map((quest, index) => (
                <motion.div
                  key={quest.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 + index * 0.03 }}
                  className="flex gap-4 p-4 rounded-2xl bg-card/50 shadow-sm border border-border/30"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shrink-0 shadow-sm">
                    <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground/70 line-clamp-1">{quest.quest_templates.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {quest.completed_at ? new Date(quest.completed_at).toLocaleDateString('id-ID') : '-'}
                      </span>
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold">
                        +{quest.quest_templates.xp_reward} XP
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {quests.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4 shadow-sm">
              <BookOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">Belum Ada Modul</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Modul pembelajaran akan muncul di sini ketika tersedia.
            </p>
            <Button onClick={() => navigate('/quests')} variant="outline" className="rounded-xl shadow-sm">
              Lihat Quest Lainnya
            </Button>
          </motion.div>
        )}
      </main>
    </div>
  );
}